import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Button } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { SsoIconComponent } from './sso-icon/sso-icon.component';

/**
 * Reusable SSO buttons component for Google and Apple login
 *
 * Usage:
 * <app-sso-buttons (success)="onLoginSuccess()" />
 */
@Component({
  selector: 'app-sso-buttons',
  standalone: true,
  imports: [CommonModule, Button, Divider, TranslatePipe, SsoIconComponent],
  templateUrl: './sso-buttons.html',
  styleUrl: './sso-buttons.scss'
})
export class SsoButtonsComponent implements OnDestroy {
  @Output() success = new EventEmitter<void>();
  @Input() showDivider = true;

  googleLoading = false;
  appleLoading = false;

  private cleanupFunctions: (() => void)[] = [];

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  ngOnDestroy(): void {
    // Cleanup all event listeners and intervals
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
  }

  /**
   * Handle Google Sign-In
   * Opens OAuth popup and handles the response
   */
  loginWithGoogle(): void {
    this.openOAuthPopup('google', 'Google Sign-In');
  }

  /**
   * Handle Apple Sign-In
   * Opens OAuth popup and handles the response
   */
  loginWithApple(): void {
    this.openOAuthPopup('apple', 'Apple Sign-In');
  }

  /**
   * Generic OAuth popup handler
   */
  private openOAuthPopup(provider: 'google' | 'apple', windowTitle: string): void {
    const loadingKey = `${provider}Loading` as 'googleLoading' | 'appleLoading';
    this[loadingKey] = true;

    // Get the OAuth URL from backend
    this.authService.getOAuthUrl(provider).subscribe({
      next: (response: { url: string }) => {
        this.handleOAuthFlow(provider, response.url, windowTitle);
      },
      error: (error) => {
        console.error(`Failed to get ${provider} auth URL:`, error);
        this.toastService.error(this.translate.instant(`auth.${provider}_login_failed`));
        this[loadingKey] = false;
      }
    });
  }

  /**
   * Handle OAuth flow - open popup and listen for response
   */
  private handleOAuthFlow(provider: 'google' | 'apple', url: string, windowTitle: string): void {
    const loadingKey = `${provider}Loading` as 'googleLoading' | 'appleLoading';

    // Open OAuth URL in popup window
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      url,
      windowTitle,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      this.toastService.error(this.translate.instant('auth.popup_blocked'));
      this[loadingKey] = false;
      return;
    }

    // Listen for message from popup
    const messageHandler = (event: MessageEvent) => {
      // Accept only from the popup we opened (works when popup is on www vs apex or another path vs parent origin check)
      if (event.source !== popup) {
        return;
      }

      if (event.data.type === `${provider}-auth-success` && event.data.token) {
        this.handleAuthToken(provider, event.data.token);
        popup?.close();
        window.removeEventListener('message', messageHandler);
      } else if (event.data.type === `${provider}-auth-error`) {
        this[loadingKey] = false;
        this.toastService.error(this.translate.instant(`auth.${provider}_login_failed`));
        popup?.close();
        window.removeEventListener('message', messageHandler);
      }
    };

    window.addEventListener('message', messageHandler);

    // Cleanup if popup is closed manually
    const checkPopupClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkPopupClosed);
        window.removeEventListener('message', messageHandler);
        this[loadingKey] = false;
      }
    }, 500);

    // Store cleanup function
    this.cleanupFunctions.push(() => {
      clearInterval(checkPopupClosed);
      window.removeEventListener('message', messageHandler);
      popup?.close();
    });
  }

  /**
   * Finalize OAuth login using backend-issued auth token
   */
  private handleAuthToken(provider: 'google' | 'apple', token: string): void {
    const loadingKey = `${provider}Loading` as 'googleLoading' | 'appleLoading';

    this.authService.completeOAuthLogin(token).subscribe({
      next: () => {
        this[loadingKey] = false;
        this.success.emit();
      },
      error: (error) => {
        console.error(`${provider} login error:`, error);
        this.toastService.error(this.translate.instant(`auth.${provider}_login_failed`));
        this[loadingKey] = false;
      }
    });
  }
}

